import { SearchFilter } from "@simplerealtybits/srb-types";
import { AppointmentSlotRequestDto } from "@t/Common/Dtos/Appointments/AppointmentSlotRequestDto";
import { BranchManagerReportFilter } from "@t/Common/Filters/BranchManagerReportFilter";
import { GetTargetsRequestDto } from "@t/GetTargetsRequestDto";

export const companyQueryKeys = {
    getById: (companyId: number | null) => ["company", companyId] as const,
    // Add more company-related keys here
};

export const userQueryKeys = {
    getByFilter: (filter: SearchFilter) => ["users", "filter", filter] as const,
    getOptionsByFilter: (filter: SearchFilter) => ["users", "options-filter", filter] as const,
    getFullByFilter: (filter: SearchFilter) => ["users", "full-filter", filter] as const,
    getAllOptions: () => ["users", "all-options"] as const,
    getActiveOptions: () => ["users", "active-options"] as const,
    getById: (userId: number | null) => ["users", userId] as const,
    getOutsideSalesPersonnel: () => ["users", "outside-sales"] as const,
    getManagers: () => ["users", "managers"] as const,
    // Add more user-related keys here
};

export const referralQueryKeys = {
    getById: (referralId: number | null) => ["referrals", referralId] as const,
    getByFilter: (filter: SearchFilter) => ["referrals", "filter", filter] as const,
    // Add more referral-related keys here
};

export const officeQueryKeys = {
    getByFilter: (filter: SearchFilter) => ["offices", "filter", filter] as const,
    getById: (officeId: number | null) => ["offices", officeId] as const,
    // Add more office-related keys here
};

export const leadSourceQueryKeys = {
    getByFilter: (filter: SearchFilter | null) => ["lead-sources", "filter", filter] as const,
    getById: (leadSourceId: number | null) => ["lead-sources", leadSourceId] as const,
    // Add more lead source-related keys here
};

export const leadTypeQueryKeys = {
    getByFilter: (filter: SearchFilter | null) => ["lead-types", "filter", filter] as const,
    getById: (leadTypeId: number | null) => ["lead-types", leadTypeId] as const,
    // Add more lead type-related keys here
};

export const leadQueryKeys = {
    getById: (leadId: number | null) => ["leads", leadId] as const,
    getStats: (filter: SearchFilter = {}) => ["leads", "stats", filter] as const,
    getAppointments: (leadId: number) => ["leads", leadId, "appointments"] as const,
    getSubscriptions: (leadId: number) => ["leads", leadId, "subscriptions"] as const,
    getBundles: (leadId: number) => ["leads", leadId, "bundles"] as const,
    getNotes: (leadId: number) => ["leads", leadId, "notes"] as const,
    getDuplicates: (lead: any) => ["leads", "duplicates", lead] as const,
    getOldLeadStats: (officeIds?: number[], minDate?: string, maxDate?: string) => {
        const positiveIds = officeIds?.filter((id) => id > 0);
        const normalized =
            positiveIds && positiveIds.length > 0
                ? Array.from(new Set(positiveIds)).sort((a, b) => a - b)
                : null;
        return ["leads", "old-lead-stats", normalized, minDate ?? null, maxDate ?? null] as const;
    },
    getDueFollowUps: (through?: string) => ["leads", "follow-ups", "due", through ?? null] as const,
};

export const commercialLeadQueryKeys = {
    getById: (leadId: number | null) => ["commercial-leads", leadId] as const,
};

export const propertyInspectionQueryKeys = {
    search: (filter: SearchFilter) => ["property-inspections", "search", filter] as const,
    getById: (inspectionId: string | null) => ["property-inspections", inspectionId] as const,
    getByIdPublic: (inspectionId: string | null) =>
        ["property-inspections", "public", inspectionId] as const,
};

export const subscriptionTemplateQueryKeys = {
    search: (filter: SearchFilter) => ["subscription-templates", "search", filter] as const,
    searchLean: (filter: SearchFilter) =>
        ["subscription-templates", "search-lean", filter] as const,
    getById: (templateId: number | null) => ["subscription-templates", templateId] as const,
    getPricingPlanById: (planId: number | null) =>
        ["subscription-templates", "pricing-plans", planId] as const,
};

export const schedulingQueryKeys = {
    getOpenSlots: (filter: AppointmentSlotRequestDto) =>
        ["scheduling", "open-slots", filter] as const,
    getUserSchedule: (userId: number, date: Date) =>
        ["scheduling", "user-schedule", userId, date.toISOString()] as const,
};

export const contractQueryKeys = {
    getById: (contractId: string | null) => ["contracts", contractId] as const,
    getByLeadId: (leadId: number | null) => ["contracts", "lead", leadId] as const,
    getByDoorknockId: (doorKnockEventId: number | null) =>
        ["contracts", "doorknock", doorKnockEventId] as const,
    getPdf: (contractId: string | null) => ["contracts", contractId, "pdf"] as const,
};

export const pestQueryKeys = {
    getByFilter: (filter: SearchFilter) => ["pests", "filter", filter] as const,
};

export const lookupQueryKeys = {
    getByFilter: (filter: SearchFilter) => ["lookups", "filter", filter] as const,
    getById: (lookupId: number | null) => ["lookups", lookupId] as const,
};

export const productQueryKeys = {
    getByFilter: (filter: SearchFilter) => ["products", "filter", filter] as const,
    getByFilterPublic: (filter: SearchFilter) => ["products", "public-filter", filter] as const,
};

export const jobSpecialtyQueryKeys = {
    getByFilter: (filter: SearchFilter) => ["job-specialties", "filter", filter] as const,
};

export const wiseTackQueryKeys = {
    getPromoForAmount: (amount: number) => ["wisetack", "promo", amount] as const,
    getByLeadId: (leadId: number) => ["wisetack", "transactions", "lead", leadId] as const,
};

export const authQueryKeys = {
    getTrustedDevices: () => ["auth", "trusted-devices"] as const,
};

export const userTaskQueryKeys = {
    getByFilter: (filter: SearchFilter) => ["user-tasks", "filter", filter] as const,
    getById: (taskId: number | null) => ["user-tasks", taskId] as const,
    getViewerById: (taskId: number | null) => ["user-tasks", "viewer", taskId] as const,
    getViewerSubscriptionsById: (taskId: number | null) =>
        ["user-tasks", "viewer", "subscriptions", taskId] as const,
    getCategories: () => ["user-tasks", "categories"] as const,
    getStats: (filter: SearchFilter) => ["user-tasks", "stats", filter] as const,
    getPerformanceReport: (filter: SearchFilter) =>
        ["user-tasks", "performance-report", filter] as const,
    getAverageHandlingTimeReport: (filter: SearchFilter) =>
        ["user-tasks", "average-handling-time-report", filter] as const,
};

export const userActivityQueryKeys = {
    getByFilter: (filter: SearchFilter) => ["user-activities", "filter", filter] as const,
    getById: (activityId: number | null) => ["user-activities", activityId] as const,
};

export const teamsQueryKeys = {
    getByFilter: (filter: SearchFilter) => ["teams", "filter", filter] as const,
    getById: (teamId: number | null) => ["teams", teamId] as const,
};

export const mapAreaQueryKeys = {
    polygon: {
        search: (filter: SearchFilter) => ["map-polygons", "search", filter] as const,
        d2dCurrentTerritory: () => ["map-polygons", "d2d-current-territory"] as const,
    },
    ospByLatLng: (lat: number, lng: number) => ["map-areas", "osp-by-latlng", lat, lng] as const,
};

export const officeCoverageQueryKeys = {
    layer: () => ["office-coverage", "layer"] as const,
    stats: (version: string, latitude: number, longitude: number) =>
        ["office-coverage", "stats", version, latitude, longitude] as const,
};

export const customerQueryKeys = {
    getById: (customerId: number | null) => ["customer", "get-by-id", customerId] as const,
    d2dMapSearch: (filter: SearchFilter) => ["call-logs", "d2d-map-search", filter] as const,
};

export const callLogQueryKeys = {
    search: (filter: SearchFilter) => ["call-logs", "search", filter] as const,
    getById: (callLogId: number | null) => ["call-logs", callLogId] as const,
    getTranscript: (callTranscriptDto: any) =>
        ["call-logs", "transcript", callTranscriptDto] as const,
};

export const doorKnockQueryKeys = {
    search: (filter: SearchFilter) => ["door-knock-events", "search", filter] as const,
    searchRecent: (filter: SearchFilter) => ["door-knock-events", "search-recent", filter] as const,
    getById: (doorKnockId: number | null) => ["door-knock-events", doorKnockId] as const,
    getLeaderboard: (filter: any) => ["door-knock-events", "leaderboard", filter] as const,
    getDashboardStats: (mapPolygonId: number) =>
        ["door-knock-events", "dashboard-stats", mapPolygonId] as const,
};

export const targetQueryKeys = {
    all: ["targets"] as const,
    getTargets: (filter?: GetTargetsRequestDto) =>
        [...targetQueryKeys.all, "get-targets", filter] as const,
    search: (filter: SearchFilter) => [...targetQueryKeys.all, "search", filter] as const,
    searchIdeal: (filter: SearchFilter) =>
        [...targetQueryKeys.all, "search-ideal", filter] as const,
};

export const reportingQueryKeys = {
    getBranchManagerReport: (filter: BranchManagerReportFilter) =>
        ["reporting", "branch-manager", filter] as const,
    getBranchCentricUserReport: (
        filter: BranchManagerReportFilter,
        officeId: number | null | undefined
    ) => ["reporting", "branch-centric-users", officeId, filter] as const,
    getUserCentricLeadsReport: (
        filter: BranchManagerReportFilter,
        userId: number | null | undefined
    ) => ["reporting", "user-centric-leads", userId, filter] as const,
    getLeadHoldReport: () => ["reporting", "lead-hold-report"] as const,
};

export const tagQueryKeys = {
    search: (filter: SearchFilter) => ["tags", "search", filter] as const,
    getById: (tagId: number | null) => ["tags", tagId] as const,
};

export const brandingQueryKeys = {
    search: (filter?: SearchFilter) => ["branding", "search", filter] as const,
    getById: (brandingId: number | null) => ["branding", brandingId] as const,
};

export const commercialContractQueryKeys = {
    getById: (contractId: string | null) => ["commercial-contracts", contractId] as const,
    getDraftItems: (contractId: string | null) =>
        ["commercial-contracts", contractId, "draft-items"] as const,
    getByLeadId: (leadId: number | null) => ["commercial-contracts", "lead", leadId] as const,
};

export const communicationsQueryKeys = {
    all: ["communications"] as const,
    conversations: (filter: unknown) =>
        ["communications", "conversations", "list", filter] as const,
    conversationsRoot: () => ["communications", "conversations", "list"] as const,
    conversationMessages: (conversationId: number) =>
        ["communications", "conversations", "messages", conversationId] as const,
    counts: () => ["communications", "conversations", "counts"] as const,
    unreadCount: () => ["communications", "conversations", "unread-count"] as const,
};
